/**
 * Documents Page - RAG Document Management
 * Interface for uploading documents and querying them with semantic search
 * Validates: Requirements 14.1, 14.2, 14.3, 14.4, 14.5
 * Validates: Phase 3 - Context Management & Caching
 */
import { useState } from 'react';
import { useDocumentStore } from '../stores/documentStore';
import { useConversationStore } from '../stores/conversationStore';
import { DocumentUpload } from '../components/documents/DocumentUpload';
import { DocumentList } from '../components/documents/DocumentList';
import { RAGChat } from '../components/documents/RAGChat';
import { ConversationList } from '../components/conversations/ConversationList';
import { ConversationChat } from '../components/conversations/ConversationChat';
import type { Conversation } from '../types';

export function DocumentsPage() {
  const { error, clearError } = useDocumentStore();
  const { currentConversation, setCurrentConversation } = useConversationStore();
  const [activeTab, setActiveTab] = useState<'quick-query' | 'conversations'>('quick-query');

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800 bg-gray-900/50">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <svg
              className="w-6 h-6 text-purple-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <h1 className="text-xl font-semibold text-white">Documents & RAG</h1>
          </div>
          <div className="text-sm text-gray-400">
            Upload and query documents with semantic search
          </div>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="mx-4 mt-4 bg-red-500/10 border border-red-500/50 rounded-lg p-4 flex items-start justify-between">
          <div className="flex items-start gap-3">
            <svg
              className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            <div>
              <p className="font-medium text-red-500">Error</p>
              <p className="text-sm text-red-400 mt-1">{error}</p>
            </div>
          </div>
          <button
            onClick={clearError}
            className="text-red-400 hover:text-red-300"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Upload & Document List - Always visible */}
          <DocumentUpload />
          <DocumentList />

          {/* Tabbed Interface */}
          <div className="bg-gray-800/50 rounded-lg border border-gray-700">
            {/* Tabs */}
            <div className="flex border-b border-gray-700">
              <button
                onClick={() => setActiveTab('quick-query')}
                className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                  activeTab === 'quick-query'
                    ? 'text-purple-400 border-b-2 border-purple-500 bg-gray-900/50'
                    : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800/30'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  Quick Query
                </div>
              </button>
              <button
                onClick={() => setActiveTab('conversations')}
                className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                  activeTab === 'conversations'
                    ? 'text-purple-400 border-b-2 border-purple-500 bg-gray-900/50'
                    : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800/30'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                  </svg>
                  Conversations
                  <span className="ml-1 text-xs bg-purple-600/30 text-purple-300 px-1.5 py-0.5 rounded">
                    New
                  </span>
                </div>
              </button>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {activeTab === 'quick-query' ? (
                <div className="space-y-4">
                  <div className="text-sm text-gray-400 mb-4">
                    One-off queries without conversation context. Results are not saved to history.
                  </div>
                  <RAGChat />
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-6 min-h-[600px]">
                  {/* Conversation List - Left Sidebar */}
                  <div className="col-span-1">
                    <ConversationList
                      onSelectConversation={(conv: Conversation) => setCurrentConversation(conv)}
                      selectedConversationId={currentConversation?.id}
                    />
                  </div>

                  {/* Conversation Chat - Main Area */}
                  <div className="col-span-2">
                    {currentConversation ? (
                      <ConversationChat conversation={currentConversation} />
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full text-gray-400">
                        <svg
                          className="w-16 h-16 mb-4 text-gray-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                          />
                        </svg>
                        <p className="text-lg font-medium">Select a conversation</p>
                        <p className="text-sm text-gray-500 mt-1">
                          Or create a new one to get started
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Info Section */}
          <div className="bg-gray-800/30 rounded-lg p-6 border border-gray-700">
            <h3 className="text-lg font-medium text-white mb-3">Features</h3>
            <div className="grid grid-cols-2 gap-4 text-sm text-gray-300">
              <div className="flex gap-3">
                <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-purple-600 text-white text-xs font-bold">
                  1
                </span>
                <div>
                  <p className="font-medium text-white">Quick Query</p>
                  <p className="text-gray-400 text-xs mt-1">
                    One-off document searches with hybrid search and re-ranking
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-purple-600 text-white text-xs font-bold">
                  2
                </span>
                <div>
                  <p className="font-medium text-white">Conversations</p>
                  <p className="text-gray-400 text-xs mt-1">
                    Multi-turn conversations with context tracking and smart pruning
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-purple-600 text-white text-xs font-bold">
                  3
                </span>
                <div>
                  <p className="font-medium text-white">Context Management</p>
                  <p className="text-gray-400 text-xs mt-1">
                    Automatic token tracking with context window monitoring
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-purple-600 text-white text-xs font-bold">
                  4
                </span>
                <div>
                  <p className="font-medium text-white">Smart Caching</p>
                  <p className="text-gray-400 text-xs mt-1">
                    Redis-backed caching for faster queries and better performance
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
